import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Users, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Json } from "@/integrations/supabase/types";

interface AppSettingRow {
  key: string;
  value: Json;
}

interface BringFriendConfig {
  is_active: boolean;
  default_eligible: boolean;
  max_seats_per_inviter: number;
  invite_expiry_days: number;
  campaign_starts_at: string | null;
  campaign_ends_at: string | null;
}

const DEFAULT_CONFIG: BringFriendConfig = {
  is_active: false,
  default_eligible: false,
  max_seats_per_inviter: 3,
  invite_expiry_days: 3,
  campaign_starts_at: null,
  campaign_ends_at: null,
};

const toLocalInput = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function BringFriendSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["app-settings", "bring_friend_config"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("key, value")
        .eq("key", "bring_friend_config")
        .maybeSingle();
      if (error) throw error;
      return data as AppSettingRow | null;
    },
  });

  const config = (data?.value as unknown as Partial<BringFriendConfig>) ?? {};
  const merged: BringFriendConfig = { ...DEFAULT_CONFIG, ...config };

  const [isActive, setIsActive] = useState(false);
  const [defaultEligible, setDefaultEligible] = useState(false);
  const [maxSeats, setMaxSeats] = useState("3");
  const [expiryDays, setExpiryDays] = useState("3");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");

  useEffect(() => {
    setIsActive(merged.is_active);
    setDefaultEligible(merged.default_eligible);
    setMaxSeats(String(merged.max_seats_per_inviter));
    setExpiryDays(String(merged.invite_expiry_days));
    setStartsAt(toLocalInput(merged.campaign_starts_at));
    setEndsAt(toLocalInput(merged.campaign_ends_at));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const value = {
        is_active: isActive,
        default_eligible: defaultEligible,
        max_seats_per_inviter: Math.max(
          1,
          Math.min(10, parseInt(maxSeats) || 3),
        ),
        invite_expiry_days: Math.max(
          1,
          Math.min(30, parseInt(expiryDays) || 3),
        ),
        campaign_starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        campaign_ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      };
      const { error } = await supabase
        .from("app_settings")
        .update({
          value: value as unknown as Json,
          updated_at: new Date().toISOString(),
          updated_by: user?.id ?? null,
        })
        .eq("key", "bring_friend_config");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["app-settings", "bring_friend_config"],
      });
      queryClient.invalidateQueries({
        queryKey: ["bring-friend-eligibility"],
      });
      toast({
        title: "Saved",
        description: "Bring a Friend settings updated.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message ?? "Failed to save.",
        variant: "destructive",
      });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Bring a Friend for Free
        </CardTitle>
        <CardDescription>
          Control the Bring a Friend for Free campaign. Eligibility can also be
          set per event and per series.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">
                  Campaign active
                </Label>
                <p className="text-sm text-muted-foreground">
                  When on, eligible events show the Bring a Friend panel. When
                  off, all BAFFF features are hidden.
                </p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base font-medium">
                  Default eligible
                </Label>
                <p className="text-sm text-muted-foreground">
                  When on, all events are eligible by default unless overridden
                  per event or series.
                </p>
              </div>
              <Switch
                checked={defaultEligible}
                onCheckedChange={setDefaultEligible}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bff-max-seats">
                  Max friend seats per inviter
                </Label>
                <Input
                  id="bff-max-seats"
                  type="number"
                  min={1}
                  max={10}
                  value={maxSeats}
                  onChange={(e) => setMaxSeats(e.target.value)}
                  className="max-w-[160px]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bff-expiry-days">Invite expiry (days)</Label>
                <Input
                  id="bff-expiry-days"
                  type="number"
                  min={1}
                  max={30}
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(e.target.value)}
                  className="max-w-[160px]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bff-starts-at">
                  Campaign starts (optional)
                </Label>
                <Input
                  id="bff-starts-at"
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bff-ends-at">Campaign ends (optional)</Label>
                <Input
                  id="bff-ends-at"
                  type="datetime-local"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
